/*
 * Product compare for the Katan Palace storefront.
 *
 * The selection lives in localStorage and the comparison is built from
 * /products/<handle>.js, so no app or backend is required. Every string and row
 * switch comes from Admin (theme settings plus the compare section's settings)
 * and is read here from data attributes.
 */
(() => {
  const config = document.getElementById('kp-compare-config');
  if (!config) return;

  const STORE_KEY = 'kp-compare';
  const limit = Math.max(2, Number(config.dataset.limit) || 3);
  const moneyFormat = config.dataset.moneyFormat || '{{ amount }}';
  const locale = config.dataset.locale || undefined;
  const pageUrl = config.dataset.pageUrl || '';

  const esc = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const decimals = new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const money = (cents) => {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';
    return moneyFormat
      .replace(/<[^>]*>/g, '')
      .replace(/\{\{\s*amount_with_commas\s*\}\}|\{\{\s*amount\s*\}\}/g, decimals.format(value / 100))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}|\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(value / 100)));
  };

  const readSelection = () => {
    try {
      const raw = JSON.parse(localStorage.getItem(STORE_KEY) || '[]');
      return Array.isArray(raw) ? raw.filter((h) => typeof h === 'string' && h).slice(0, limit) : [];
    } catch {
      return [];
    }
  };

  let handles = readSelection();

  const save = () => {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(handles));
    } catch {
      /* storage blocked (private mode): the selection still works for this page view */
    }
  };

  const cache = new Map();
  const loadProduct = (handle) => {
    if (!cache.has(handle)) {
      cache.set(
        handle,
        fetch(`/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      );
    }
    return cache.get(handle);
  };

  const compareUrl = (list) => {
    if (!pageUrl || list.length < 2) return '';
    const query = list.map((handle, index) => `p${index + 1}=${encodeURIComponent(handle)}`).join('&');
    return `${pageUrl}${pageUrl.includes('?') ? '&' : '?'}${query}`;
  };

  /* Product fetches and tray renders are async, so every write checks that the
     selection it was started with is still the current one. */
  let trayTicket = 0;
  let tableTicket = 0;
  const sameList = (a, b) => a.length === b.length && a.every((h, i) => h === b[i]);

  /* ------------------------------------------------------------- selection UI */

  const tray = document.getElementById('kp-compare-tray');
  const status = tray?.querySelector('[data-kp-compare-status]');

  const say = (key) => {
    if (status && key) status.textContent = tray.dataset[key] || '';
  };

  const inputs = () => [...document.querySelectorAll('[data-kp-compare-input]')];

  const renderTray = async () => {
    if (!tray) return;
    const slots = tray.querySelector('[data-kp-compare-slots]');
    const cta = tray.querySelector('[data-kp-compare-go]');
    const clear = tray.querySelector('[data-kp-compare-clear]');
    const heading = tray.querySelector('[data-kp-compare-heading]');
    const note = tray.querySelector('[data-kp-compare-note]');

    const href = compareUrl(handles);
    cta.href = href;
    cta.toggleAttribute('hidden', !href);
    clear.toggleAttribute('hidden', !handles.length);
    heading.textContent = `${tray.dataset.heading} (${handles.length})`;
    tray.classList.toggle('is-open', handles.length > 0);
    tray.inert = handles.length === 0;

    if (!handles.length) {
      ++trayTicket;
      slots.replaceChildren();
      note.textContent = '';
      return;
    }

    const snapshot = [...handles];
    const run = ++trayTicket;
    const items = await Promise.all(snapshot.map(loadProduct));
    if (run !== trayTicket || !sameList(snapshot, handles)) return;

    const list = items
      .map((data, index) => {
        const handle = snapshot[index];
        const title = data?.title || handle;
        const image = data?.featured_image || data?.images?.[0] || '';
        const li = document.createElement('li');
        li.className = 'kp-compare-tray__item';
        if (image) {
          const img = document.createElement('img');
          img.src = image;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 34;
          img.height = 42;
          li.append(img);
        }
        const name = document.createElement('span');
        name.className = 'kp-compare-tray__name';
        name.textContent = title;
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'kp-compare-tray__remove';
        remove.dataset.kpCompareRemove = handle;
        remove.setAttribute('aria-label', `${tray.dataset.removeLabel} ${title}`);
        remove.textContent = '×';
        li.append(name, remove);
        return li;
      });
    slots.replaceChildren(...list);

    const full = handles.length >= limit && inputs().some((input) => !input.checked);
    note.textContent = full ? tray.dataset.limitNote : '';
  };

  const paint = () => {
    inputs().forEach((input) => {
      const chosen = handles.includes(input.dataset.kpCompareInput);
      input.checked = chosen;
      input.disabled = !chosen && handles.length >= limit;
    });
    renderTray();
  };

  document.addEventListener('change', (event) => {
    const input = event.target.closest?.('[data-kp-compare-input]');
    if (!input) return;
    const handle = input.dataset.kpCompareInput;

    if (input.checked) {
      if (handles.includes(handle)) return;
      if (handles.length >= limit) {
        input.checked = false;
        paint();
        say('limitNote');
        return;
      }
      handles = [...handles, handle];
      say('addedNote');
    } else {
      handles = handles.filter((h) => h !== handle);
      say('removedNote');
    }
    save();
    paint();
  });

  tray?.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-kp-compare-remove]');
    if (remove) {
      handles = handles.filter((h) => h !== remove.dataset.kpCompareRemove);
      save();
      paint();
      say('removedNote');
      return;
    }
    if (event.target.closest('[data-kp-compare-clear]')) {
      handles = [];
      save();
      paint();
      say('clearedNote');
    }
  });

  /* ------------------------------------------------------------------- table */

  const table = document.querySelector('[data-kp-compare-table]');
  const attr = (name, fallback = '') => table?.getAttribute(`data-${name}`) ?? fallback;

  const fromQuery = () => {
    const params = new URLSearchParams(window.location.search);
    const list = [];
    for (let index = 1; index <= limit; index += 1) {
      const handle = params.get(`p${index}`);
      if (handle) list.push(handle);
    }
    return list;
  };

  // Merchant HTML is only ever read for its text, never attached to the page.
  const toText = (html) => {
    const doc = new DOMParser().parseFromString(`<body>${html || ''}</body>`, 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  };

  // /products/<handle>.js returns options as { name, position, values } objects.
  const optionNames = (p) => (p.options || []).map((option) => option?.name ?? option).filter(Boolean);

  const rows = [
    {
      key: 'price',
      cell: (p, word) => {
        const list = p.variants || [];
        const wasAt = Math.max(Number(p.compare_at_price_max) || 0, ...list.map((v) => Number(v.compare_at_price) || 0));
        const was = wasAt > Number(p.price) ? money(wasAt) : '';
        const varies = p.price_varies ? ` <span class="kp-compare-table__note">${esc(word.varies)}</span>` : '';
        return `<span class="kp-compare-table__price">${esc(money(p.price))}${varies}</span>${was ? `<span class="kp-compare-table__was">${esc(was)}</span>` : ''}`;
      },
      same: (p) => `${p.price}/${p.price_min}/${p.price_max}`,
    },
    {
      key: 'availability',
      cell: (p, word) =>
        p.available
          ? `<span class="kp-compare-table__flag">${esc(word.inStock)}</span>${(p.variants || []).some((v) => !v.available) ? ` <span class="kp-compare-table__note">${esc(word.partial)}</span>` : ''}`
          : `<span class="kp-compare-table__flag">${esc(word.outOfStock)}</span>`,
      same: (p) => `${p.available}/${(p.variants || []).map((v) => v.available).join(',')}`,
    },
    {
      key: 'options',
      cell: (p, word) => esc(optionNames(p).join(', ') || word.none),
      same: (p) => optionNames(p).join('|'),
    },
    {
      key: 'variants',
      cell: (p, word) => {
        const list = p.variants || [];
        if (!list.length) return esc(word.none);
        return `<ul class="kp-compare-table__list">${list
          .map((v) => {
            const name = (v.options || []).filter(Boolean).join(' / ') || v.title || '';
            const state = v.available ? word.inStock : word.outOfStock;
            return `<li>${esc(name)} — ${esc(money(v.price))} · ${esc(state)}${v.sku ? ` · ${esc(v.sku)}` : ''}</li>`;
          })
          .join('')}</ul>`;
      },
      same: (p) => (p.variants || []).map((v) => `${(v.options || []).join('/')}:${v.price}:${v.available}`).join('|'),
    },
    { key: 'type', cell: (p, word) => esc(p.type || word.none), same: (p) => p.type || '' },
    { key: 'vendor', cell: (p, word) => esc(p.vendor || word.none), same: (p) => p.vendor || '' },
    { key: 'tags', cell: (p, word) => esc((p.tags || []).join(', ') || word.none), same: (p) => [...(p.tags || [])].sort().join('|') },
    {
      key: 'description',
      cell: (p, word) => {
        const text = toText(p.description);
        if (!text) return esc(word.none);
        return esc(text.length > 320 ? `${text.slice(0, 320).trimEnd()}…` : text);
      },
      same: (p) => toText(p.description),
    },
  ];

  const word = () => ({
    inStock: attr('word-in-stock'),
    outOfStock: attr('word-out-of-stock'),
    partial: attr('word-partial'),
    varies: attr('word-varies'),
    none: attr('word-none'),
  });

  const renderTable = async (list) => {
    if (!table) return;
    const mount = table.querySelector('[data-kp-compare-mount]');
    const empty = table.querySelector('[data-kp-compare-empty]');

    if (list.length < 2) {
      mount.innerHTML = '';
      mount.hidden = true;
      empty.hidden = false;
      return;
    }

    empty.hidden = true;
    mount.hidden = false;
    mount.innerHTML = `<div class="kp-compare-skeleton" role="status" aria-label="${esc(attr('loading-note'))}"></div>`;

    const run = ++tableTicket;
    const results = await Promise.all(list.map(loadProduct));
    if (run !== tableTicket) return;
    const products = results.map((data, index) => (data ? { ...data, handle: list[index] } : null));
    if (!products.some(Boolean)) {
      mount.innerHTML = `<p>${esc(attr('failed-note'))}</p>`;
      return;
    }

    const terms = word();
    const shown = rows.filter((row) => attr(`show-${row.key}`) !== 'false');

    const head = `<tr>
      <th scope="col" class="kp-compare-table__corner"><span class="kp-compare-visually-hidden">${esc(attr('corner-label'))}</span></th>
      ${products
        .map((p, index) => {
          const handle = p?.handle || list[index];
          const title = p?.title || handle;
          const image = p?.featured_image || p?.images?.[0] || '';
          const missing = p ? '' : `<p>${esc(attr('missing-note'))}</p>`;
          const soldOut = p && !p.available ? `<p><span class="kp-compare-table__flag">${esc(terms.outOfStock)}</span></p>` : '';
          const href = p?.url || `/products/${handle}`;
          return `<th scope="col"><figure>${image ? `<img src="${esc(image)}" alt="${esc(title)}" loading="lazy" width="190" height="238">` : ''}<figcaption><a class="kp-compare-table__link" href="${esc(href)}">${esc(title)}</a></figcaption>${soldOut}${missing}</figure></th>`;
        })
        .join('')}
    </tr>`;

    const body = shown
      .map((row) => {
        const marks = products.map((p) => (p ? row.same(p) : ''));
        const identical = products.filter(Boolean).length > 1 && marks.every((value) => value === marks[0] && value !== '');
        const cells = products
          .map((p) => (p ? `<td>${row.cell(p, terms)}</td>` : `<td><span style="opacity:.55">${esc(terms.none)}</span></td>`))
          .join('');
        const match = identical ? ` <span class="kp-compare-table__same">${esc(attr('match-note'))}</span>` : '';
        return `<tr><th scope="row">${esc(attr(`label-${row.key}`))}${match}</th>${cells}</tr>`;
      })
      .join('');

    mount.innerHTML = `<div class="kp-compare-table__wrap"><table class="kp-compare-table"><thead>${head}</thead><tbody>${body}</tbody></table></div>`;
  };

  paint();
  document.addEventListener('shopify:section:load', paint);
  if (table) {
    const requested = fromQuery();
    renderTable(requested.length >= 2 ? requested : handles);
  }
})();
